<script lang="ts">
  // TODO(GCR): Add automatic SEO via https://schema.org/VideoObject
  import { browser } from '$app/env';
  import { onDestroy, onMount } from 'svelte';
  import VideoJs from 'video.js';
  import { createEventDispatcher } from 'svelte';
  import 'video.js/dist/video-js.min.css';
  import './video-js.postcss';
  import H from '../H.svelte';

  export let videoSources: string[];
  export let autoplay: boolean;

  let error: { code: number; message: string } | undefined = undefined;

  interface $$Events extends Omit<Partial<HTMLButtonElement>, 'click'> {
    video404: string;
    videoEnded: null;
  }
  const dispatch = createEventDispatcher<{
    video404: string;
    videoEnded: null;
  }>();

  interface ReloadSourceOnErrorArgument {
    getSource?: (reload: Function) => void;
    errorInterval?: number;
  }

  // Merge reloadSourceOnError
  interface PlayerWithReload extends VideoJs.Player {
    reloadSourceOnError: (reloadArg?: ReloadSourceOnErrorArgument) => void;
  }

  $: sources = videoSources.map((src) => ({
    src,
    type: src.includes('.m3u8') ? 'application/x-mpegURL' : 'video/mp4',
  }));

  let currentSourceIndex = 0;

  $: {
    createPlayer(sources);
  }

  let player: PlayerWithReload;
  let videoDiv: HTMLDivElement;

  onMount(() => {
    if (!browser) return;
    createPlayer(sources);
  });

  function createPlayer(sources: { src: string; type: string }[]) {
    if (!videoDiv || !browser) return;
    if (player) player.dispose();
    videoDiv.innerHTML = '';
    const video = document.createElement('video');
    video.setAttribute('crossorigin', 'anonymous');
    video.classList.add('video-js', 'vjs-16-9', 'vjs-big-play-centered');
    videoDiv.append(video);

    player = VideoJs(
      video,
      {
        controls: true,
        autoplay,
        preload: 'auto',
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        sources,
      },
      // The player on-ready function
      playerReady
    ) as PlayerWithReload;

    player.on('error', videoErrorHandler);

    /**
     * Handle an error thrown by the player or its video element. We expect these to be
     * one of the media errors here:
     * https://www.w3.org/TR/2009/WD-html5-20090825/video.html#error-codes
     */
    function videoErrorHandler() {
      const playerError = player.error() || {
        code: 0,
        message: 'An unknown video error occurred.',
      };

      // If it's an error code 2, 3, 4, let's try another source:
      // Error codes described at: https://www.w3.org/TR/2009/WD-html5-20090825/video.html#error-codes
      if ([2, 3, 4].includes(playerError.code)) {
        if (sources.length > currentSourceIndex + 1) {
          currentSourceIndex += 1;
          const nextSources: VideoJs.Tech.SourceObject[] = sources.slice(currentSourceIndex);
          player.src(nextSources);
        } else {
          error = {
            code: playerError.code,
            message: `The video could not be loaded, either because the network failed or because
            your browser blocked the download of ${sources.map((src) => src.src).join(' and ')}}`,
          };
          dispatch('video404', error.message);
        }
        return;
      }

      // if (error?.code > 2 || error?.code === 0) {
      //   Rollbar.error(error);
      // }
      const errorMessage =
        playerError.code !== 4
          ? playerError.message
          : `The video could not be loaded, either because the network failed or because
            your browser blocked the download of ${sources.map((src) => src.src).join(' and ')}}`;
      console.error(errorMessage);
      dispatch('video404', errorMessage);
      error = playerError;
    }

    function playerReady() {
      const userSettings = getVideoPreferences();

      player.on('timeupdate', () => {
        const tracks = Array.from(player.textTracks());

        if (userSettings.textTracks && player.currentTime() < 0.5) {
          const tracks = Array.from(player.textTracks());
          tracks.forEach((track, index) => {
            track.mode = getVideoPreferences().textTracks[index]?.isVisible ? 'showing' : 'hidden';
          });
          return;
        }

        // Update user prefs based on text tracks:
        const newTrackPrefs: VideoJSUserSettings['textTracks'] = tracks.map((t, i) => ({
          trackNumber: i,
          isVisible: t.mode === 'showing',
        }));

        if (JSON.stringify(newTrackPrefs) !== JSON.stringify(getVideoPreferences().textTracks)) {
          setVideoPreferences({
            textTracks: newTrackPrefs,
          });
        }
      });

      if (userSettings.fullscreen) {
        player.requestFullscreen();
      }

      player.on('fullscreenchange', () => {
        if (player.currentTime() < 1) return;
        setVideoPreferences({ fullscreen: player.isFullscreen() });
      });

      player.on('ended', () => {
        dispatch('videoEnded', null);
      });

      player.volume(userSettings.volume);
      player.on('volumechange', () => setVideoPreferences({ volume: player.volume() }));

      // This has to be in a timeout because a rate is automatically set on element creation,
      // which will unfortunately override the existing setting.
      setTimeout(() => {
        if (player.isDisposed() === true) return;
        player.playbackRate(userSettings.playbackRate);
        player.on('ratechange', (evt) => {
          setVideoPreferences({ playbackRate: video.playbackRate });
        });
      }, 200);
    }
  }

  onDestroy(() => {
    if (player) player.dispose();
  });

  interface VideoJSUserSettings {
    volume: number;
    textTracks: { trackNumber: number; isVisible: boolean }[];
    playbackRate: number;
    fullscreen: boolean;
  }

  let userSettings: VideoJSUserSettings;

  function setVideoPreferences(settings: Partial<VideoJSUserSettings>) {
    userSettings = {
      ...userSettings,
      ...settings,
    };
    try {
      window.localStorage.setItem('uTheoryVJSUserSettings', JSON.stringify(userSettings));
    } catch (err) {
      // Eat the error.
    }
  }

  function getVideoPreferences() {
    userSettings = userSettings || {
      volume: 1,
      captions: false,
      playbackRate: 1,
      fullscreen: false,
    };
    try {
      const json = window.localStorage.getItem('uTheoryVJSUserSettings');
      userSettings = {
        ...userSettings,
        ...JSON.parse(json!),
      };
    } catch (err) {
      // Eat it.
    }
    return userSettings;
  }
</script>

{#if error}
  <div class="bg-red-10">
    <H level={2}>Error Loading Video</H>
    <p>{error.message}</p>
  </div>
{:else}
  <div bind:this={videoDiv} />
{/if}
